import { lazy } from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { UnifiedAuthProvider } from '@/contexts/UnifiedAuthContext';
import { AdminAuthProvider } from '@/contexts/AdminAuthContext';
import { UnifiedProtectedRoute } from '@/components/unified/UnifiedProtectedRoute';

const AdminDashboard = lazy(() => import('@/pages/admin/Dashboard'));
const AdminInvoices = lazy(() => import('@/pages/admin/Invoices'));
const AdminReports = lazy(() => import('@/pages/admin/Reports'));
const AdminSettings = lazy(() => import('@/pages/admin/Settings'));
const AdminApiKeys = lazy(() => import('@/pages/admin/ApiKeys'));
const AdminUsers = lazy(() => import('@/pages/admin/Users'));
const AdminProjects = lazy(() => import('@/pages/admin/Projects'));
const AdminBilling = lazy(() => import('@/pages/admin/Billing'));
const AdminBillingUpload = lazy(() => import('@/pages/admin/BillingUpload'));
const AdminBillingBatchDetail = lazy(() => import('@/pages/admin/BillingBatchDetail'));
const AdminPaymentReports = lazy(() => import('@/pages/admin/PaymentReports'));
const AdminDailyRoutes = lazy(() => import('@/pages/admin/DailyRoutes'));
const AdminContracts = lazy(() => import('@/pages/admin/Contracts'));
const AdminSubContracts = lazy(() => import('@/pages/admin/SubContracts'));
const AdminLiveOps = lazy(() => import('@/pages/admin/LiveOps'));
const AdminDrivers = lazy(() => import('@/pages/admin/DriversAdmin'));
const AdminFleets = lazy(() => import('@/pages/admin/Fleets'));
const AdminVehicles = lazy(() => import('@/pages/admin/VehiclesAdmin'));
const AdminMatching = lazy(() => import('@/pages/admin/Matching'));
const AdminClients = lazy(() => import('@/pages/admin/Clients'));
const AdminPayout = lazy(() => import('@/pages/admin/Payout'));

// Any user with at least one admin role may access the portal.
// Fine-grained role checks happen inside individual page components.
const ADMIN_ROLES = ['super_admin', 'finance', 'operations', 'viewer'];

function AdminShell() {
  return (
    <UnifiedProtectedRoute requiredRoles={ADMIN_ROLES}>
      <Outlet />
    </UnifiedProtectedRoute>
  );
}

export function AdminRoutes() {
  return (
    <UnifiedAuthProvider>
      <AdminAuthProvider>
        <Routes>
          {/* Redirect legacy admin login to the unified login page */}
          <Route path="login" element={<Navigate to="/app/login" replace />} />

          {/* All admin pages protected by unified auth + admin role */}
          <Route element={<AdminShell />}>
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="invoices" element={<AdminInvoices />} />
            <Route path="reports" element={<AdminReports />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="api-keys" element={<AdminApiKeys />} />
            <Route path="projects" element={<AdminProjects />} />
            <Route path="billing" element={<AdminBilling />} />
            <Route path="billing/upload" element={<AdminBillingUpload />} />
            <Route path="billing/:batchId" element={<AdminBillingBatchDetail />} />
            <Route path="payment-reports" element={<AdminPaymentReports />} />
            <Route path="daily-routes" element={<AdminDailyRoutes />} />
            <Route path="contracts" element={<AdminContracts />} />
            <Route path="sub-contracts" element={<AdminSubContracts />} />
            <Route path="liveops" element={<AdminLiveOps />} />
            <Route path="drivers" element={<AdminDrivers />} />
            <Route path="fleets" element={<AdminFleets />} />
            <Route path="vehicles" element={<AdminVehicles />} />
            <Route path="matching" element={<AdminMatching />} />
            <Route path="clients" element={<AdminClients />} />
            <Route path="payout" element={<AdminPayout />} />
          </Route>

          <Route path="" element={<Navigate to="/admin/dashboard" replace />} />
        </Routes>
      </AdminAuthProvider>
    </UnifiedAuthProvider>
  );
}
